<template>
  <div class="dashboard-ad-table">
    <loading-icon v-if="adsLoading"/>

    <table v-else-if="hasData" class="table-wrapper">
      <thead>
        <th v-for="(col, index) in getColumnsPropValues('label')" :key="index" class="header">
          {{col}}
        </th>
      </thead>
      <tbody>
        <tr v-for="(ad, index) in ads" :key="index" class="body-row">
          <td v-for="(key, index) in getColumnsPropValues('key')" :key="index" :class="`${key}-td`" @click="cellClicked(key === 'name', ad)">
            <div v-if="ad[key] === 'loading' || ad[key] == undefined" :class="`${key}-cell cell`">
              <loading-icon />
            </div>
            <div v-else-if="key !== 'retailers'" :class="`${key}-cell cell`">
              {{getCellText(key, ad)}}
            </div>
            <div v-else class="cell">
              <tooltip position="top">
                <div slot="content" class="tooltip-content">
                  <h6 class="tooltip-header">
                    {{`retailers sent (${ad.retailers.retailers_sent})`}}
                  </h6>
                  <div v-if="(ad.retailers.retailers_names && ad.retailers.retailers_names.length > 0)"
                      class="tooltip-retailers">
                    <h6 v-for="(name, index) in ad.retailers.retailers_names"
                        :key="index"
                        class="tooltip-retailer">
                      {{name}}
                    </h6>
                  </div>
                  <h6 v-else class="no-retailers">
                    no retailers
                  </h6>
                </div>
                {{ad.retailers.retailers_sent}}
              </tooltip>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <h3 v-else class="message">{{ adsError || 'no ads to display' }}</h3>

    <modal
      v-if="showModal"
      :showActionButton="false"
      showClose
      wrapperClasses="performance-modal"
      :show="showModal"
      @close="showModal = false"
      @proceed="showModal = false">
      <div slot="header" class="modal-header">
         <div class="modal-title">
          {{ modalConfig[0].name }}
        </div>
        <sb-table
          class="text-centered"
          :columns="columns.slice(1, columns.length)"
          :rows="modalConfig"
          >
            <template #cell="{ row, column }">
              <div v-if="column.key === 'texts_remaining'">
                {{ row.texts_remaining }}<template v-if="row.unsent">(unsent)</template>
              </div>
              <div v-else>
                {{ column.key === "retailers" ? row[column.key].retailers_sent : row[column.key] }}
              </div>
            </template>
          </sb-table>
        <div class="link">
          <router-link :to="`/performance/${modalConfig[0].id}`" exact>performance</router-link>
        </div>
      </div>
      <div slot="body" class="image-preview-wrapper">
        <img :src="modalConfig[0].image_url" alt="uploaded-image">
      </div>
    </modal>

    <pagination
      v-if="showPagination"
      :currentPage="currentPage"
      :totalPageCount="totalPageCount"
      showArrows
      @changePage="changePage"/>
  </div>
</template>

<script>
  import LoadingIcon from '../Shared/LoadingIcon'
  import Modal from '../Shared/Modal.vue'
  import moment from 'moment'
  import Pagination from '../Shared/Pagination.vue'
  import Tooltip from '../Shared/Tooltip'
  import { adAPI } from '../../services/api/adAPI'
  import { brandsAPI } from '../../services/api/brands.ts'
  import { compare } from '../../utilities'
  import { NumberFormatters } from '../mixins/NumberFormatters.js'

  export default {
    props: {
      selectedTab: Object,
      dateRange: Object,
      currentAdvertiserId: Number
    },

    name: "DashboardAdTable",

    mixins: [NumberFormatters],

    components: {
      Modal,
      LoadingIcon,
      Tooltip,
      Pagination,
    },

    data(){
      return {
        columns:[
          { label:'', key:'name' },
          { label:'date', key:'date' },
          { label:'retailers', key:'retailers' },
          { label:'msgs sent', key: 'messages_sent'},
          { label:'msgs left', key: 'texts_remaining'},
          { label:'cost', key: 'purchases'},
          { label: 'units sold', key: 'units_sold'},
          { label:'revenue', key: 'revenue'}
        ],
        modalConfig: [],
        showModal: false,
        adsError: null,
        adsLoading: false,
        ads: [],
        totalPageCount: null,
        currentPage: 1,
        endOfToday: moment.utc().endOf('day').unix(),
      }
    },

    computed: {
      hasData() {
        return this.ads?.length && !this.adsError
      },
      showPagination() {
        return !this.adsLoading && this.totalPageCount > 1
      }
    },

    created() {
      this.getAds()
    },

    methods: {
      cellClicked(isAdNameClicked, ad) {
        isAdNameClicked ? this.displayModal(ad) : this.redirectToPerformancePage(ad.id)
      },

      changePage(page) {
        this.currentPage = page
        this.getAds()
      },

      closeModal() {
        this.showModal = false
      },

      displayModal(row){
        this.modalConfig[0] = row
        return this.showModal = true
      },

      getCellText(cellType, ad) {
        const extraText = cellType === 'texts_remaining' && ad.unsent ? ' (unsent)' : ''
        return `${ad[cellType]}${extraText}`
      },

      getColumnsPropValues(key) {
        return this.columns.map(col => col[key])
      },

      redirectToPerformancePage(id){
        return this.$router.push({ path: `/performance/${id}`})
      },

      async getAds() {
        const params = {
          brand_id: this.currentAdvertiserId,
          end_time: this.dateRange.end,
          page: this.currentPage,
          scope: this.selectedTab.key,
          start_time: this.dateRange.start,
        }
        try {
          this.adsLoading = true
          const data = await adAPI.getAds(params)
          this.adsLoading = false
          this.ads = this.generatePerformanceKPIRows(data.ads)
          this.ads = await Promise.all(this.ads.map(ad => this.parseKpiRow(ad)))
          if (data.pagination) {
            this.totalPageCount = data.pagination?.total_pages || 1
          }
        } catch(e) {
          this.adsError = e
          this.adsLoading = false
        }
      },

      async parseKpiRow(ad) {
        ad.units_sold = await this.getAdKpi({ key: 'total_units', ad })
        ad.revenue = await this.getAdKpi({ key: 'revenue_generated', ad, useCurrency: true })
        ad.retailers = await this.getAdKpi({ key: 'retailers_sent', ad, shouldShorten: false, shouldReturnWholeValue: true })
        ad.retailers.retailers_names?.sort(compare)

        const messages_sent = await this.getAdKpi({ key: 'messages_sent', ad, shouldShorten: false })
        ad.purchases = `$${this.numberWithCommas((messages_sent * ad.cost_per_message).toFixed(2))}`
        ad.messages_sent = this.shortenNumber(messages_sent)
        return ad
      },

      async getAdKpi({ key, ad, shouldShorten = true, shouldReturnWholeValue = false, useCurrency }) {
        let result
        try {
          const params = {
            end_time: this.dateRange.end,
            brand_id: this.currentAdvertiserId,
            brand_campaign_id: ad.id,
            start_time: this.dateRange.start
          }
          const adKpiData = await brandsAPI.getPerformanceAdKPI(key, params)

          if (shouldReturnWholeValue) { result = adKpiData }
          else if (shouldShorten) { result = this.shortenNumber(adKpiData[key], useCurrency) }
          else { result = adKpiData[key] }
        } catch(e) {
          result = 0
        } finally { return result }
      },

      generatePerformanceKPIRows(ads) {
        const extraDataKeys = ['units_sold', 'revenue', 'retailers', 'messages_sent', 'purchases']
        return ads.map(ad => {
          ad.unsent = this.endOfToday > moment(ad.send_at_end).unix() && !!ad.credits_remaining
          ad.texts_remaining = this.shortenNumber(ad.credits_remaining)
          ad.date = `${moment(ad.send_at_start).utc().format('MM/DD/YY')} - ${moment(ad.send_at_end).utc().format('MM/DD/YY')}`
          extraDataKeys.forEach(key => ad[key] = 'loading')
          return ad
        })
      },
    },

    watch: {
      dateRange: {
        handler() {
          this.currentPage = 1
          this.getAds()
        },
        deep: true
      },

      selectedTab: {
        handler(newValue) {
          if (newValue.key !== 'request') {
            this.currentPage = 1
            this.getAds()
          }
        },
        deep: true
      }
    }
  }
</script>

<style lang="sass">
@import "../../assets/sass/colors.sass"
.dashboard-ad-table
  .table-wrapper
    border-collapse: separate
    border-spacing: 0 10px
    margin-top: 2em
    position: relative
    width: 100%
    &::before
      border-left: 10px solid $primary-color
      border-radius: 5px
      bottom: -5%
      content: ""
      left: 8%
      position: absolute
      top: 0
      z-index: -1
    th
      padding: 0 8px
    td
      padding: 0
      text-align: center
      vertical-align: middle
      .loading-icon
        margin: 0 auto
        svg
          height: 14px
    .cell
      border-top: 1px solid $primary-color
      border-bottom: 1px solid $primary-color
      padding: 8px
      position: relative
    .name-cell
      background-color: white
      border-radius: 1em
      border: 1px solid $primary-color
      margin-right: 20px
      min-width: 80px
      overflow-wrap: anywhere
      position: relative
      word-break: normal
      &::before
        border-top: 1px solid $primary-color
        content: ""
        position: absolute
        right: -10px
        top: 50%
        width: 10px
    .date-cell
      background: white
      border-bottom-left-radius: 1em
      border-left: 1px solid $primary-color
      border-top-left-radius: 1em
      white-space: nowrap
    .texts_remaining-cell
      white-space: nowrap
    .revenue-cell
      border-bottom-right-radius: 1em
      border-right: 1px solid $primary-color
      border-top-right-radius: 1em
    .header
      font-family: 'AileronLight'
      font-size: 1rem
      text-align: center
      width: calc(100%/8)
    .body-row
      cursor: pointer
      &:hover
        .cell
          background-color: $primary-color
          color: white
        .loading-icon svg
          fill: white
        .tooltip
          color: white
    .tooltip
      color: $primary-color
      display: flex
      justify-content: center
      text-decoration: underline
      .tooltip-text
        bottom: 160%
        width: 150px
      .tooltip-retailers
        max-height: 100px
        overflow: auto
        padding: 5px
        text-align: left
      .tooltip-retailer, .no-retailers
        color: $title-color
      .tooltip-header
        font-weight: bold
        margin-bottom: 5px
      .tooltip-retailer
        word-wrap: break-word
        &:not(:last-child)
        margin-bottom: 5px
      .no-retailers
        text-align: center
  .message
    margin-top: 30px
</style>